import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, Plus } from "lucide-react";

const SUGGESTED = [
  "Networking", "Small Business", "Marketing", "Real Estate", "Finance",
  "Technology", "Health & Wellness", "Coaching", "Sales", "Hospitality",
  "Creative", "Nonprofit", "Legal", "Coffee", "Community", "Fitness",
  "Education", "Faith",
];

const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/**
 * Reusable interest/tag picker: tap suggested chips or add your own.
 * Controlled — `value` is a string[]; `onChange` receives the next array.
 */
export function InterestsPicker({ value = [], onChange, max = 12 }) {
  const [custom, setCustom] = useState("");
  const selected = value || [];

  const toggle = (t) => {
    if (selected.includes(t)) onChange(selected.filter((x) => x !== t));
    else if (selected.length < max) onChange([...selected, t]);
  };

  const addCustom = () => {
    const t = custom.trim();
    if (!t) return;
    if (!selected.some((x) => x.toLowerCase() === t.toLowerCase()) && selected.length < max) {
      onChange([...selected, t]);
    }
    setCustom("");
  };

  const options = Array.from(new Set([...SUGGESTED, ...selected]));

  return (
    <div data-testid="interests-picker">
      <div className="flex flex-wrap gap-1.5">
        {options.map((t) => {
          const on = selected.includes(t);
          return (
            <button
              type="button"
              key={t}
              onClick={() => toggle(t)}
              aria-pressed={on}
              data-testid={`interest-option-${slug(t)}`}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                on
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-foreground hover:border-primary/50"
              }`}
            >
              {on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              {t}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex gap-2">
        <Input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
          placeholder="Add your own…"
          maxLength={28}
          data-testid="interest-custom-input"
        />
        <Button type="button" variant="secondary" onClick={addCustom} disabled={!custom.trim() || selected.length >= max} data-testid="interest-add-button">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{selected.length}/{max} selected</p>
    </div>
  );
}
